// src/pages/Following/FollowersList.tsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../../shared/contexts/AuthContext";
import api from "../../../shared/api/axios";
import FollowButtonMobile from "../../components/FollowButtonMobile";
import { useTranslation } from "react-i18next";

interface LiteUser {
  _id: string;
  profilename: string;
  picture?: string;
  createdAt?: string;
}

interface FollowerRow {
  follower: LiteUser;   // who follows you
  createdAt: string;    // when they followed you
}

interface ApiFollowersResponse {
  users?: LiteUser[];
  items?: { follower: LiteUser; createdAt: string }[];
}

const FollowersListMobile: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
const location = useLocation();

  const [entries, setEntries] = useState<FollowerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  // kebab/dropdown state
  const [actionUserId, setActionUserId] = useState<string | null>(null);
  const [blockBusyId, setBlockBusyId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const [confirmState, setConfirmState] = useState<{
  mode: "block" | "report";
  userId: string;
  profilename?: string;
} | null>(null);

  const baseUrl =
    process.env.REACT_APP_API_BASE_URL || "http://localhost:3001";
  const getAvatarUrl = (path?: string) =>
    path ? (path.startsWith("http") ? path : `${baseUrl}${path}`) : "";

  // Normalize server response into FollowerRow[] (keep server order)
  const normalize = (data: ApiFollowersResponse): FollowerRow[] => {
    if (Array.isArray(data.items) && data.items.length) {
      return data.items
        .filter((r) => r?.follower?._id)
        .map((r) => ({
          follower: {
            _id: r.follower._id,
            profilename: r.follower.profilename,
            picture: r.follower.picture,
          },
          createdAt: r.createdAt,
        }));
    }

    if (Array.isArray(data.users) && data.users.length) {
      return data.users
        .filter((u) => u?._id)
        .map((u) => ({
          follower: {
            _id: u._id,
            profilename: u.profilename,
            picture: u.picture,
          },
          createdAt: (u as any).createdAt || "",
        }));
    }

    return [];
  };

  useEffect(() => {
    const fetchFollowers = async () => {
      if (!user?._id) return;
      try {
        setLoading(true);
        const res = await api.get<ApiFollowersResponse>(
          `/follows/${user._id}/followers`,
          {
            params: { offset: 0, limit: 500, includeMeta: 1 },
          }
        );
        
        setEntries(normalize(res.data));
      } catch (err) {
        console.error("❌ Failed to fetch followers:", err);
        setEntries([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFollowers();
  }, [user?._id]);

  // close dropdown on outside click
  useEffect(() => {
  if (!actionUserId) return;

  const handler = (e: MouseEvent) => {
    const target = e.target as Node;
    if (!menuRef.current?.contains(target)) {
      setActionUserId(null);
    }
  };

  document.addEventListener("pointerdown", handler, { passive: true });

  return () => {
    document.removeEventListener("pointerdown", handler);
  };
}, [actionUserId]);

useEffect(() => {
  setActionUserId(null);
  setBlockBusyId(null);
}, []);

useEffect(() => {
  document.body.style.pointerEvents = "auto";
  return () => {
    document.body.style.pointerEvents = "auto";
  };
}, []);


  // Filter by search query (no sorting, keeps server order)
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return entries;
    return entries.filter((r) =>
      r.follower.profilename.toLowerCase().includes(term)
    );
  }, [entries, q]);

  // --- Block helpers (parallel to FollowingList) ---

  const blockUser = async (targetId: string): Promise<boolean> => {
  if (!targetId || user?._id === targetId) return false;

  try {
    setBlockBusyId(targetId);
    await api.post(`/block-report/block/${encodeURIComponent(targetId)}`);
    alert(t('followers.block.success'));
    return true;
  } catch (e: any) {
    alert(
      e?.response?.data?.error ||
      e?.response?.data?.msg ||
      e?.message ||
      t('followers.block.failed')
    );
    return false;
  } finally {
    setBlockBusyId(null);
    setActionUserId(null);
  }
};

const blockAndReportUser = (targetId: string, profilename?: string) => {
  setConfirmState({ mode: "report", userId: targetId, profilename });
};

  return (
<div style={pageWrap} key={location.key}>
    

      {/* Right content area */}
      <div style={contentCol}>
        <div style={{ marginTop: "0%" }}>
          <h2 style={{ color: "#ccc", margin: 0 }}>{t('followers.title')}</h2>

          {/* 🔎 Search below title */}
          <div style={{ marginTop: 20, maxWidth: 420 }}>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('followers.search.placeholder')}
              aria-label={t('followers.search.ariaLabel')}
              style={searchInput}
            />
          </div>
        </div>

        {/* List */}
        {loading ? (
          <p style={{ color: "#aaa", marginTop: 20 }}>{t('followers.states.loading')}</p>
        ) : filtered.length === 0 ? (
          <p style={{ color: "#aaa", marginTop: 20 }}>
          {q ? t('followers.states.noMatches') : t('followers.states.noFollowers')}
          </p>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 14,
              marginTop: 22,
            }}
          >
            {filtered.map((row) => {
              const u = row.follower;
              const avatar = getAvatarUrl(u.picture);
              const hasDate = !!row.createdAt;
              const open = actionUserId === u._id;
              const busy = blockBusyId === u._id;

              return (
                <div
                  key={u._id}
                  style={itemRow}
                  onClick={(e) => {
  if ((e.target as HTMLElement).closest("button")) return;
  navigate(`/visitor-profile/${u._id}`);
}}
                >
                  <div style={avatarStyle(avatar)}>
                    {!u.picture && "👻"}
                  </div>

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      flex: 1,
                    }}
                  >
                   <span style={{ color: "#bbb", fontWeight: 600, fontSize: 14 }}>

                      {u.profilename}
                    </span>
                    <span style={{ color: "#999", fontSize: 10 }}>

                    {t('followers.row.followedYou')}{' '}
                      {hasDate
                        ? `${new Date(
                            row.createdAt
                          ).toLocaleDateString()} ${new Date(
                            row.createdAt
                          ).toLocaleTimeString()}`
                          : t('followers.row.noDate')}
                    </span>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      position: "relative",
                    }}
                    ref={open ? menuRef : undefined}
                  >
                    <div
  onClick={(e) => e.stopPropagation()}
  style={{ transform: "scale(1.05)", transformOrigin: "center" }}
>
  <FollowButtonMobile
    authorId={u._id}
    profilename={u.profilename}
  />
</div>

                    <button
                      style={viewBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/visitor-profile/${u._id}`);
                      }}
                    >
                      {t('followers.buttons.view')}
                    </button>

                    {/* ⋮ menu on the RIGHT of View */}
                    <button
                      style={{
                        ...kebabBtn,
                        ...(busy
                          ? { opacity: 0.6, cursor: "wait" }
                          : null),
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setActionUserId((prev) =>
                          prev === u._id ? null : u._id
                        );
                      }}
                      disabled={busy}
                      aria-label={t('followers.buttons.moreActions')}

                    >
                      ⋮
                    </button>

                    {open && (
                      <div
  style={dropdown}
  onMouseDown={(e) => e.stopPropagation()}
  onClick={(e) => e.stopPropagation()}
>

  <button
  type="button"
  style={dropdownItem}
  onClick={(e) => {
    e.stopPropagation();
    setConfirmState({ mode: "block", userId: u._id, profilename: u.profilename });
  }}
>

  🚫 {t('followers.dropdown.block')}
</button>

<button
  type="button"
  style={{ ...dropdownItem, color: "#ff6b6b" }}
  onClick={async (e) => {
    e.stopPropagation();
    await blockAndReportUser(u._id, u.profilename);
  }}
>
  🛑 {t('followers.dropdown.blockAndReport')}
</button>


                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {confirmState && (
  <div style={confirmOverlay}>
    <div style={confirmCard}>
      <div style={{ color: "#ccc", fontSize: 15, marginBottom: 14 }}>
        {t("followers.block.confirm", {
          displayName: confirmState.profilename
            ? `@${confirmState.profilename}`
            : t("followers.block.displayNameFallback"),
        })}
      </div>

      <button
        style={primaryBtn}
        onClick={async () => {
          const ok = await blockUser(confirmState.userId);

          if (ok && confirmState.mode === "report") {
            navigate(`/report?targetType=user&targetId=${confirmState.userId}`);
          }

          setConfirmState(null);
        }}
      >
        ✅ {t("common.confirm")}
      </button>

      <button style={cancelBtn} onClick={() => setConfirmState(null)}>
        ❌ {t("common.cancel")}
      </button>
    </div>
  </div>
)}

    </div>
  );
};

/* ---------------- Styles ---------------- */

const pageWrap: React.CSSProperties = {
  display: "flex",
  backgroundColor: "#000",
  height: "100vh",
  overflow: "hidden",
};

const contentCol: React.CSSProperties = {
  flex: 1,
  padding: 14,
  overflowY: "auto",
  height: "100vh",
  boxSizing: "border-box",
};


const searchInput: React.CSSProperties = {
  width: "93%",
  padding: "8px 10px",
  borderRadius: 9,
  border: "1px solid #333",
  backgroundColor: "#111",
  color: "#ddd",
  outline: "none",
  fontSize: 16,
};

const itemRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  backgroundColor: "#1a1a1a",
  padding: "8px 10px",
  borderRadius: 10,
  cursor: "pointer",
  transition: "background 0.2s",
};

const avatarStyle = (imgUrl?: string): React.CSSProperties => ({
  width: 34,
  height: 34,
  borderRadius: 8,
  backgroundImage: imgUrl ? `url(${imgUrl})` : "none",
  backgroundColor: imgUrl ? "transparent" : "rgb(38, 29, 162)",
  backgroundSize: "cover",
  backgroundPosition: "center",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: imgUrl ? 0 : 18,
  color: "#fff",
});


const baseBtn: React.CSSProperties = {
  backgroundColor: "#111",
  color: "#ccc",
  border: "1px solid #333",
  borderRadius: 8,
  padding: "8px 10px",
  cursor: "pointer",
};

const viewBtn: React.CSSProperties = {
  ...baseBtn,
  padding: "3px 7px",
  fontSize: 13,
};

/* 3-dots button */
const kebabBtn: React.CSSProperties = {
  ...baseBtn,
  padding: "4px 8px",
  fontSize: 16,
  lineHeight: 1,
};

/* Dropdown menu */
const dropdown: React.CSSProperties = {
  position: "absolute",
  top: "100%",
  right: 0,
  marginTop: 8,
  backgroundColor: "#111",
  borderRadius: 12,
  border: "1px solid #333",
  boxShadow: "0 8px 24px rgba(0,0,0,0.7)",
  display: "flex",
  flexDirection: "column",
  minWidth: 200,     // ⬅️ bigger width
  zIndex: 999999,  
  overflow: "hidden",
};

const dropdownItem: React.CSSProperties = {
  padding: "14px 16px",
  background: "#111",
  color: "#eee",
  border: "none",
  textAlign: "left",
  cursor: "pointer",
  fontSize: 16,
  fontWeight: 600,
  transition: "background 0.15s ease",
};


const primaryBtn: React.CSSProperties = {
  width: "100%",
  padding: 14,
  borderRadius: 10,
  border: "none",
  background: "#5b21b6",
  color: "#fff",
  fontWeight: 700,
  marginBottom: 10,
};

const cancelBtn: React.CSSProperties = {
  width: "100%",
  padding: 12,
  borderRadius: 10,
  border: "none",
  background: "transparent",
  color: "#aaa",
};
const confirmOverlay: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.85)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 99999999,
};

const confirmCard: React.CSSProperties = {
  width: "90%",
  maxWidth: 420,
  background: "#111",
  borderRadius: 14,
  padding: 20,
  border: "1px solid #333",
  textAlign: "center",
};

export default FollowersListMobile;
